<template>
  <div
    :class="state.inAddMode ? 'form-container' : 'group-details-form'"
  >
    <h2
      v-if="state.inAddMode"
      data-test="AddGroupTitle"
      class="page-title-heading"
    >
      {{ $localize('AddGroupTitle') }}
    </h2>
    <div class="row">
      <div class="col-6">
        <div class="form-group">
          <component
:is="state.inAddMode ? 'h3' : 'h4'"
            data-test="GroupName"
            for="groupName"
            :class="['required', 'page-title-heading', { 'mt-3' :state.inAddMode }]"
          >
            {{ $localize('GroupName') }}
          </component>
          <TextInputField
            id="groupName"
            v-model="state.groupName"
            :aria-label="$localize('GroupName')"
            :validation-rules="state.validationRules?.groupName"
            :error-id="'groupNameValidationMessage'"
          />
        </div>
      </div>
      <div
        v-if="state.inEditMode && state.showLocation"
        class="col-6"
      >
        <div>
          <h4 class="page-title-heading">{{ $localize('LocationTitle') }}</h4>
          <div class="location-text">
            <div
              v-if="state.address"
              class="ellipsis"
            >
              <i
                v-if="state.address"
                placement="bottom"
              >
                {{ state.address }}
              </i>
            </div>
            <span v-else>{{ $localize('NoLocationAssigned') }}</span>
          </div>
        </div>
      </div>
      <div class="col-xs-12">
        <TabMenu
          v-if="state.showLocation"
          v-model="state.selectedTabItem"
          :items="state.tabItems"
        />
        <h4 v-else class="page-title-heading">
          {{ $localize('Users') }}
        </h4>
      </div>
      <div class="col-xs-12">
        <div
          v-if="state.isSelectedTabUsers"
          class="listbox-container"
        >
          <DualListBox
            v-model:available-items="state.availableUsers"
            v-model:assigned-items="state.assignedUsers"
            :loading="false"
            :selected-item-id="selectedItemId"
            @is-checked-updated="handleIsCheckedUpdated"
          />
        </div>
        <LocationTab
          v-else
          ref="locationTab"
          :address="state.address"
          :coordinate="state.coordinate"
          :is-location-assigned="state.isLocationAssigned"
          :initial-coordinate="state.initialCoordinate"
          :validation-rules="state.validationRules"
          @is-location-assigned-updated="handleIsLocationAssignedUpdate"
          @group-address-updated="handleGroupAddressUpdate"
          @map-details-saved="handleMapDetailsUpdate"
        />
      </div>
    </div>
    <ClientErrorList
      :error-keys="state.clientErrors"
      :show-horizontal-rule="state.inAddMode"
    />
    <ServerErrorList :errors="state.serverErrors" />
    <div class="row">
      <div class="col-xs-12 text-end">
        <VueButton
          v-if="state.inAddMode"
          :type="VueButtonTypes.primary"
          :loading="state.formSubmitting"
          aria-describedby="groupNameValidationMessage clientSideValidationErrorMessage serverSideValidationErrorMessage"
          data-test="SaveGroupButton"
          @click="initiateAddGroup"
        >
          {{ $localize('AddGroup') }}
        </VueButton>
        <VueButton
          v-if="state.inEditMode"
          :type="VueButtonTypes.default"
          :loading="state.groupDeleting"
          class="with-space"
          data-test="DeleteGroupButton"
          @click="initiateDeleteGroup"
        >
          {{ $localize('DeleteGroup') }}
        </VueButton>
        <VueButton
          v-if="state.inEditMode"
          :type="VueButtonTypes.primary"
          :loading="state.formSubmitting"
          aria-describedby="groupNameValidationMessage clientSideValidationErrorMessage serverSideValidationErrorMessage"
          data-test="SaveGroupButton"
          @click="initiateUpdateGroup"
        >
          {{ $localize('SaveChanges') }}
        </VueButton>
      </div>
    </div>
    <GroupDeleteConfirmationModal
      v-model="state.showGroupDeleteConfirmationModal"
      data-test="DeleteGroupConfirmationModal"
      @continue="handleContinue()"
    />
    <GroupSaveWarningModal
      v-model="state.showGroupSaveWarningModal"
      :pre-registered-users-assigned-count="state.preRegisteredUsersAssignedCountComputed"
      data-test="SaveGroupWarningModal"
      @save="handleSave()"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, watch, reactive, useTemplateRef } from 'vue';
  import { ApplicationError } from '@/Services/Clients/applicationError';
  import GroupsService from '@/Services/groupsService';
  import menuDataRepository from '@/VueComponents/Groups/menuDataRepository';
  import logger from '@/Utils/logger';
  import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
  import { useRouter } from 'vue-router';
  import constants from '@/constants';
  import PortalSettingsProvider from '@/Utils/portalSettingsProvider';
  import { AccountType } from '@/Types/Enums/accountType';
  import GroupSaveWarningModal from '@/VueComponents/Modals/GroupSaveWarningModal.vue';
  import GroupDeleteConfirmationModal from '@/VueComponents/Modals/GroupDeleteConfirmationModal.vue';
  import DualListBox from '@/VueComponents/DualListBox/DualListBox.vue';
  import TabMenu from './TabMenu.vue';
  import DualListBoxItem from '@/VueComponents/DualListBox/types/DualListBoxItem';
  import DualListBoxIconDetails from '@/VueComponents/DualListBox/types/DualListBoxIconDetails';
  import { PersonaDisplayNameSummaryDto } from '@/Models/groupManagement/personaDisplayNameSummaryDto';
  import { GroupMemberDto } from '@/Models/groupManagement/groupMemberDto';
  import { GroupDto } from '@/Models/groupManagement/groupDto';
  import LocationTab from '@/VueComponents/Groups/LocationTab.vue';
  import { GroupDetailsDto } from '@/Models/groupManagement/groupDetailsDto';
  import { GroupSummaryDto } from '@/Models/groupManagement/groupSummaryDto';
  import MapCoordinate from '@/Types/mapCoordinate';
  import VueButton from '@/VueComponents/SharedComponents/Buttons/VueButton.vue';
  import { VueButtonTypes } from '@/VueComponents/SharedComponents/Buttons/Enums/VueButtonTypes';
  import TextInputField from '@/VueComponents/SharedComponents/InputFields/TextInputField.vue';
  import ServerErrorList from '@/VueComponents/SharedComponents/ErrorList/ServerErrorList.vue';
  import ClientErrorList from '@/VueComponents/SharedComponents/ErrorList/ClientErrorList.vue';
  import { useForm } from 'vee-validate';
  import usePageLoading from '@/VueCore/utils/usePageLoading';
  import ValidationRulesService from '@/Services/validationRulesService';
  import UsersService from '@/Services/usersService';

  const contextData = useContextDataStore();
  const router = useRouter();

  const props = defineProps<{
    selectedItemId?: string | undefined,
    pageMode: string
  }>();

  const emit = defineEmits<{(e: 'updateItem', item: GroupDto): void;
                            (e: 'deleteItem', itemId: string | undefined): void;
  }>();

  const state: {
    formSubmitting: boolean;
    groupDeleting: boolean;
    inAddMode: boolean;
    inEditMode: boolean;
    showGroupDeleteConfirmationModal: boolean;
    showGroupSaveWarningModal: boolean;
    showLocation: boolean;
    serverErrors: string[];
    clientErrors: string[];
    availableUsers: DualListBoxItem[];
    assignedUsers: DualListBoxItem[];
    selectedTabItem: any;
    tabItems: any;
    selectedTabComponentName: string;
    initialCoordinate: MapCoordinate;
    defaultCoordinate: MapCoordinate;
    businessId: string;
    isSelectedTabUsers: boolean;
    preRegisteredUsersAssignedCountComputed: number;
    groupName: string;
    address?: string;
    isLocationAssigned: boolean;
    coordinate?: MapCoordinate;
    validationRules?: any;
  } = reactive({
    formSubmitting: false,
    groupDeleting: false,
    inAddMode: computed(() => props.pageMode === constants.pageMode.add),
    inEditMode: computed(() => props.pageMode === constants.pageMode.edit),
    showGroupDeleteConfirmationModal: false,
    showGroupSaveWarningModal: false,
    showLocation: computed(() => PortalSettingsProvider.getAccountType() === AccountType.Business),
    serverErrors: [],
    clientErrors: [],
    availableUsers: [],
    assignedUsers: [],
    selectedTabItem: null,
    tabItems: menuDataRepository.getGroupDetailsTabs(),
    selectedTabComponentName: 'group-users-tab',
    initialCoordinate: contextData.userData.business.coordinate || contextData.portalSettings.defaultMapView.coordinate,
    defaultCoordinate: contextData.userData.business.coordinate || contextData.portalSettings.defaultMapView.coordinate,
    businessId: contextData.userData.business.businessId,
    isSelectedTabUsers: true,
    preRegisteredUsersAssignedCountComputed: computed(() => state.assignedUsers.filter(user => user.isChecked && user.isPreRegistered).length),
    groupName: '',
    address: '',
    isLocationAssigned: false,
    coordinate: null
  });

  let allGroups: GroupSummaryDto[] = [];

  const { loadPage } = usePageLoading(async () => {
    state.validationRules = await ValidationRulesService.getValidationRules('group');
    allGroups = await GroupsService.getAllGroups(contextData.userData.business.businessId);

    state.clientErrors = [];
    state.serverErrors = [];

    setActiveTabItem();

    if (state.inAddMode) {
      const allPersonas = await UsersService.getAllPersonaSummaries(contextData.userData.business.businessId);
      state.address = '';
      state.isLocationAssigned = false;
      state.coordinate = state.defaultCoordinate;
      setAvailableUsers(allPersonas);
    } else {
      await loadGroupDetails(props.selectedItemId);
    }
  }, error => console.error('Failed to fetch validation rules or personas:', error));

  const form = useForm({
    initialValues: {
      'groupName': state.groupName
    }
  });

  const locationTab = useTemplateRef('locationTab');

  defineExpose({
    isValid
  });

  async function isValid() {
    return (await form.validate()).valid;
  }

  function setActiveTabItem() {
    const activeItem = state.tabItems.find(item => item.pageComponentName === state.selectedTabComponentName);
    if (activeItem) {
      state.tabItems.forEach(item => item.isActive = item === activeItem);
      state.selectedTabItem = activeItem;
    }
  }

  function initiateAddGroup() {
    if (allGroups && allGroups.map(group => group.groupDetails.groupName).includes(state.groupName)) {
      state.clientErrors = ['GroupNameAlreadyExists'];
      return;
    }

    if (state.preRegisteredUsersAssignedCountComputed > 0) {
      state.showGroupSaveWarningModal = true;
      return;
    }
    addGroup();
  }

  async function initiateUpdateGroup() {
    if (!(await isSavingAllowed())) {
      return;
    }

    if (state.preRegisteredUsersAssignedCountComputed > 0) {
      state.showGroupSaveWarningModal = true;
      return;
    }

    updateGroup();
  }

  async function addGroup() {
    state.serverErrors = [];
    if (!(await isSavingAllowed())) {
      return;
    }
    if (!state.isLocationAssigned) {
      resetLocation();
    }

    state.formSubmitting = true;
    const groupDetails = createGroupDetails(state.groupName, state.address, state.isLocationAssigned, state.coordinate);

    GroupsService.addNewGroup(contextData.userData.business.businessId, groupDetails, state.assignedUsers)
      .then(() => {
        logger.success('GroupAddedSummary');
        router.push('/groups');
      })
      .catch((error: ApplicationError) => {
        if (error.errorData?.errorMessages) {
          state.serverErrors = error.errorData.errorMessages;
          return;
        }
        if (!error.isLogged) {
          logger.error('UnexpectedErrorWhileUpdatingGroup', null, error);
        }
      })
      .finally(() => {
        state.formSubmitting = false;
      });
  }

  async function updateGroup() {
    state.serverErrors = [];

    if (!state.isLocationAssigned) {
      resetLocation();
    }

    state.formSubmitting = true;
    const groupDetails = createGroupDetails(state.groupName, state.address, state.isLocationAssigned, state.coordinate);

    GroupsService.updateGroup(props.selectedItemId, contextData.userData.business.businessId,
                              groupDetails, state.assignedUsers)
      .then((groupDto : GroupDto) => {        
        emit('updateItem', groupDto);
        logger.success('GroupUpdatedSummary');
      })
      .catch((error: ApplicationError) => {
        if (error.errorData?.errorMessages) {
          state.serverErrors = error.errorData.errorMessages;
          return;
        }
        if (!error.isLogged) {
          logger.error('UnexpectedErrorWhileUpdatingGroup', null, error);
        }
      })
      .finally(() => {
        state.formSubmitting = false;
      });
  }

  function handleSave() {
    state.showGroupSaveWarningModal = false;
    state.inAddMode ? addGroup() : updateGroup();
  }

  function initiateDeleteGroup() {
    state.showGroupDeleteConfirmationModal = true;
  }

  function deleteGroup() {
    state.serverErrors = [];
    state.groupDeleting = true;

    GroupsService.deleteGroup(contextData.userData.business.businessId, props.selectedItemId)
      .then(() => {
        logger.success('GroupDeletedSummary');
        emit('deleteItem', props.selectedItemId);
      })
      .catch((error:ApplicationError) => {
        if (error.errorData?.errorMessages) {
          state.serverErrors = error.errorData.errorMessages;
          return;
        }
        if (!error.isLogged) {
          logger.error('UnexpectedErrorWhileDeletingGroup', null, error);
        }
      })
      .finally(() => {
        state.groupDeleting = false;
      });
  }

  function handleContinue() {
    state.showGroupDeleteConfirmationModal = false;
    deleteGroup();
  }

  function handleIsCheckedUpdated(itemId: string, newValue: any) {
    const user = state.assignedUsers.find(user => user.id === itemId);
    if (user) {
      user.isChecked = newValue;
    }
  }

  function setAvailableUsers(availablePersonas: PersonaDisplayNameSummaryDto[] | undefined) {
    if (!availablePersonas) {
      return;
    }
    const availableUserArray: DualListBoxItem[] = availablePersonas.map(item =>
      new DualListBoxItem(
        item.personaId,
        item.personaDisplayName,
        false,
        item.isPersonaPreRegistered,
        getPersonaIconDetails(item)
      )
    );

    state.availableUsers = availableUserArray;
  }

  function setAssignedUsers(assignedGroupMembers: GroupMemberDto[]) {
    const assignedGroupMembersArray: DualListBoxItem[] = assignedGroupMembers.map(item =>
      new DualListBoxItem(
        item.memberPersona.personaId,
        item.memberPersona.personaDisplayName,
        item.isGroupPrimaryContact,
        item.memberPersona.isPersonaPreRegistered,
        getPersonaIconDetails(item.memberPersona)
      )
    );

    state.assignedUsers = assignedGroupMembersArray;
  }

  function getPersonaIconDetails(persona: any) {
    if (persona.isPersonaPreRegistered) {
      return new DualListBoxIconDetails(constants.preRegisteredUserIconClass, 'UnregisteredUserTitle');
    }
    return null;
  }

  async function loadGroupDetails(groupId: string | undefined) {
    if (props.pageMode !== constants.pageMode.edit || !groupId) {
      return;
    }

    if (groupId !== null) {
      state.serverErrors = [];

      try {
        const groupDto = await GroupsService.getGroupDetails(state.businessId, groupId);
        if (props.selectedItemId !== groupDto.groupId) {
          return;
        }

        state.groupName = groupDto.groupDetails.groupName;
        if (groupDto.groupDetails.address) {
          state.address = groupDto.groupDetails.address;
          state.isLocationAssigned = true;
        } else {
          state.address = '';
          state.isLocationAssigned = false;
        }

        const initialCoord = groupDto.groupDetails.coordinate || state.defaultCoordinate;
        state.coordinate = initialCoord;
        state.initialCoordinate = initialCoord;
        setAvailableUsers(groupDto.availablePersonas);
        setAssignedUsers(groupDto.assignedGroupMembers);
      } catch (error: ApplicationError) {
        if (error.errorData?.errorMessages?.length) {
          state.serverErrors = error.errorData.errorMessages;
        } else {
          logger.error('UnexpectedErrorWhileGettingGroup', null, error);
        }
      }
    }
  }

  async function isSavingAllowed() {
    state.clientErrors = [];
    const valid = await isValid();
    if (!valid) {
      return false;
    }
    if (locationTab && locationTab.value) {
      locationTab.value.isValid();
    }
    const validationFailureInfo = getStatusValidationInfo();
    if (validationFailureInfo) {
      state.selectedTabComponentName = validationFailureInfo.tabName;
      state.clientErrors.push(validationFailureInfo.resourceKey);
      setActiveTabItem();
      return false;
    }
    return true;
  }

  function getStatusValidationInfo() {
    if (state.assignedUsers.length > 0) {
      const groupHasPrimaryContacts = state.assignedUsers.some(item => item.isChecked);
      if (!groupHasPrimaryContacts) {
        return {
          resourceKey: 'GroupNoPrimaryContact',
          tabName: 'group-users-tab'
        };
      }
    }
    if (state.isLocationAssigned && !state.address?.trim()) {
      return {
        resourceKey: 'GroupNoAddress',
        tabName: 'group-location-tab'
      };
    }
    return null;
  }

  function resetLocation() {
    state.address = '';
    state.coordinate = null;
  }

  function handleIsLocationAssignedUpdate(newValue: any) {
    state.isLocationAssigned = newValue;
  }

  function handleGroupAddressUpdate(newValue: any) {
    state.address = newValue;
  }

  function handleMapDetailsUpdate(newValue: MapCoordinate) {
    state.coordinate = newValue;
  }

  function createGroupDetails(groupName: string, address: string | undefined, isLocationAssigned: boolean, coordinate: number[]) {
    return new GroupDetailsDto(groupName, address, isLocationAssigned, coordinate);
  }

  watch(() => state.selectedTabItem, newValue => {
    state.isSelectedTabUsers = newValue.pageComponentName === 'group-users-tab';
  });

  watch(() => props.selectedItemId, async newValue => {
    loadPage();
  });
</script>

<style scoped lang="scss">
.with-space {
  margin-right: 5px;
}

div[disabled="true"] {
  pointer-events: none;
  opacity: 0.7
}

.listbox-container {
  margin-bottom: 19px;
}
</style>